import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ChevronDown, ChevronRight, Clock, CheckCircle2, XCircle, Mail, FileText, AlertTriangle, Ban, CreditCard, Layers } from "lucide-react";

interface StripeInvoiceHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stripeInvoiceId: string;
  label?: string;
  batchRunId?: number;
}

interface StripeEvent {
  id: string;
  type: string;
  created: number;
  data: {
    object: Record<string, any>;
  };
  request?: { id: string | null } | null;
}

interface HistoryData {
  invoice: Record<string, any>;
  events: StripeEvent[];
}

interface SyntheticEntry {
  id: string;
  type: string;
  created: number;
  synthetic: true;
}

type TimelineEntry = StripeEvent | SyntheticEntry;

function buildSyntheticEntries(invoice: Record<string, any>): SyntheticEntry[] {
  const entries: SyntheticEntry[] = [];
  const st = invoice.status_transitions ?? {};
  if (invoice.created) entries.push({ id: "syn-created", type: "invoice.created", created: invoice.created, synthetic: true });
  if (st.finalized_at) entries.push({ id: "syn-finalized", type: "invoice.finalized", created: st.finalized_at, synthetic: true });
  if (st.sent_at) entries.push({ id: "syn-sent", type: "invoice.sent", created: st.sent_at, synthetic: true });
  if (st.paid_at) entries.push({ id: "syn-paid", type: "invoice.paid", created: st.paid_at, synthetic: true });
  if (st.voided_at) entries.push({ id: "syn-voided", type: "invoice.voided", created: st.voided_at, synthetic: true });
  if (st.marked_uncollectible_at) entries.push({ id: "syn-uncollectible", type: "invoice.marked_uncollectible", created: st.marked_uncollectible_at, synthetic: true });
  return entries;
}

function eventLabel(type: string): string {
  const map: Record<string, string> = {
    "invoice.created": "Invoice Created",
    "invoice.updated": "Invoice Updated",
    "invoice.finalized": "Invoice Finalized",
    "invoice.sent": "Invoice Sent",
    "invoice.paid": "Payment Succeeded",
    "invoice.payment_succeeded": "Payment Succeeded",
    "invoice.payment_failed": "Payment Failed",
    "invoice.payment_action_required": "Payment Action Required",
    "invoice.voided": "Invoice Voided",
    "invoice.marked_uncollectible": "Marked Uncollectible",
    "invoice.deleted": "Invoice Deleted",
    "payment_intent.created": "Payment Intent Created",
    "payment_intent.succeeded": "Payment Captured",
    "payment_intent.payment_failed": "Payment Failed",
    "charge.succeeded": "Charge Succeeded",
    "charge.failed": "Charge Failed",
    "charge.refunded": "Charge Refunded",
    "customer.created": "Customer Created",
    "customer.updated": "Customer Updated",
  };
  return map[type] ?? type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function EventIcon({ type }: { type: string }) {
  if (type.includes("paid") || type.includes("succeeded") || type.includes("captured")) {
    return <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />;
  }
  if (type.includes("failed") || type.includes("voided") || type.includes("uncollectible") || type.includes("deleted")) {
    return <XCircle className="w-4 h-4 text-red-500 shrink-0" />;
  }
  if (type.includes("sent")) {
    return <Mail className="w-4 h-4 text-blue-500 shrink-0" />;
  }
  if (type.includes("finalized")) {
    return <FileText className="w-4 h-4 text-indigo-500 shrink-0" />;
  }
  if (type.includes("action_required")) {
    return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />;
  }
  if (type.includes("refunded")) {
    return <CreditCard className="w-4 h-4 text-orange-500 shrink-0" />;
  }
  if (type.includes("ban") || type.includes("void")) {
    return <Ban className="w-4 h-4 text-gray-500 shrink-0" />;
  }
  return <Clock className="w-4 h-4 text-gray-400 shrink-0" />;
}

function eventMeta(event: StripeEvent): string | null {
  const obj = event.data?.object ?? {};
  if (event.type === "invoice.sent") {
    const email = obj.customer_email ?? obj.email;
    return email ? `To: ${email}` : null;
  }
  if (event.type === "invoice.paid" || event.type === "invoice.payment_succeeded") {
    const cents = obj.amount_paid ?? obj.amount_received;
    if (cents != null) return `$${(cents / 100).toFixed(2)} collected`;
  }
  if (event.type === "invoice.payment_failed") {
    const reason = obj.last_payment_error?.message ?? obj.failure_message;
    return reason ?? "Payment failed";
  }
  if (event.type === "charge.refunded") {
    const refund = obj.amount_refunded;
    if (refund != null) return `$${(refund / 100).toFixed(2)} refunded`;
  }
  return null;
}

function invoiceStatusColor(status: string) {
  const colors: Record<string, string> = {
    paid: "bg-green-100 text-green-800",
    open: "bg-blue-100 text-blue-800",
    draft: "bg-gray-100 text-gray-700",
    void: "bg-gray-100 text-gray-600",
    uncollectible: "bg-red-100 text-red-700",
  };
  return colors[status] ?? "bg-gray-100 text-gray-700";
}

export function StripeInvoiceHistoryModal({ open, onOpenChange, stripeInvoiceId, label, batchRunId }: StripeInvoiceHistoryModalProps) {
  const [rawOpen, setRawOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(true);

  const { data, isLoading, isError, error, refetch } = useQuery<HistoryData>({
    queryKey: ["/api/stripe-invoice-history", stripeInvoiceId],
    queryFn: async () => {
      const res = await fetch(`/api/stripe-invoice-history/${encodeURIComponent(stripeInvoiceId)}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Failed to fetch invoice history");
      }
      return res.json();
    },
    enabled: false,
    staleTime: 30000,
  });

  useEffect(() => {
    if (open && stripeInvoiceId) {
      refetch();
    }
  }, [open, stripeInvoiceId]);

  const inv = data?.invoice;

  // Noise types: Stripe fires invoice.updated after every mutation (adding line items,
  // finalizing, etc.) and invoiceitem.created once per line item. Neither is meaningful
  // on its own — filter invoice.updated entirely and collapse invoiceitem.created to one.
  const HIDDEN_TYPES = new Set(["invoice.updated"]);
  const FIRST_ONLY_TYPES = new Set(["invoiceitem.created"]);

  const rawEvents = [...(data?.events ?? [])].sort((a, b) => a.created - b.created);
  const seenFirstOnly = new Set<string>();
  const dedupedEvents = rawEvents.filter((e) => {
    if (HIDDEN_TYPES.has(e.type)) return false;
    if (FIRST_ONLY_TYPES.has(e.type)) {
      if (seenFirstOnly.has(e.type)) return false;
      seenFirstOnly.add(e.type);
    }
    return true;
  });

  // Synthetic entries built from the invoice's own status_transitions timestamps. These cover
  // cases where Stripe events have expired (30-day retention window) but the invoice object
  // itself still records when key milestones occurred. Suppressed by TYPE ONLY (not timestamp)
  // because status_transitions timestamps and event created values can differ by 1–2 seconds.
  const syntheticEntries: SyntheticEntry[] = inv
    ? buildSyntheticEntries(inv).filter(
        (s) => !dedupedEvents.some((e) => e.type === s.type)
      )
    : [];

  const timeline: TimelineEntry[] = [...dedupedEvents, ...syntheticEntries].sort(
    (a, b) => a.created - b.created
  );

  const modalTitle = label
    ? `Invoice History — ${label}`
    : inv?.number
    ? `Invoice History — #${inv.number}`
    : "Invoice History";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {modalTitle}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Stripe event timeline and invoice snapshot
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground text-sm">Fetching from Stripe...</span>
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
            <XCircle className="w-8 h-8 text-red-400" />
            <p className="text-sm text-red-600 font-medium">Failed to load history</p>
            <p className="text-xs text-muted-foreground">{(error as Error)?.message}</p>
          </div>
        )}

        {data && (
          <div className="flex flex-col gap-4 overflow-y-auto min-h-0 flex-1 pr-1">
            {inv && (
              <div className="rounded-lg border bg-muted/30 px-4 py-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {inv.number && (
                  <>
                    <span className="text-muted-foreground">Invoice #</span>
                    <span className="font-medium">{inv.number}</span>
                  </>
                )}
                <span className="text-muted-foreground">Status</span>
                <span>
                  <Badge className={invoiceStatusColor(inv.status)}>
                    {inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
                  </Badge>
                </span>
                {inv.amount_due != null && (
                  <>
                    <span className="text-muted-foreground">Amount Due</span>
                    <span>${(inv.amount_due / 100).toFixed(2)}</span>
                  </>
                )}
                {inv.amount_paid != null && (
                  <>
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span className="text-green-700">${(inv.amount_paid / 100).toFixed(2)}</span>
                  </>
                )}
                {inv.customer_email && (
                  <>
                    <span className="text-muted-foreground">Customer Email</span>
                    <span>{inv.customer_email}</span>
                  </>
                )}
                {inv.due_date && (
                  <>
                    <span className="text-muted-foreground">Due</span>
                    <span>{format(new Date(inv.due_date * 1000), "MMM d, yyyy")}</span>
                  </>
                )}
              </div>
            )}

            {batchRunId != null && (
              <div className="flex items-start gap-2.5 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
                <Layers className="w-4 h-4 mt-0.5 shrink-0 text-indigo-500" />
                <div>
                  <span className="font-semibold">Batch Invoice — Run #{batchRunId}</span>
                  <p className="mt-0.5 text-indigo-700 text-xs leading-snug">
                    This invoice covers <strong>multiple work orders</strong> in batch run #{batchRunId}. The total shown reflects the combined amount for the entire batch, not a single work order.
                  </p>
                </div>
              </div>
            )}

            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setTimelineOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
              >
                <span>Event Timeline</span>
                {timelineOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {timelineOpen && (
                <div className="p-4 max-h-72 overflow-y-auto">
                  {timeline.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No history available for this invoice.
                    </div>
                  ) : (
                    <ol className="relative border-l border-border ml-2">
                      {timeline.map((entry) => {
                        const isSynthetic = "synthetic" in entry && entry.synthetic;
                        const meta = isSynthetic ? null : eventMeta(entry as StripeEvent);
                        return (
                          <li key={entry.id} className="mb-4 ml-4">
                            <span className={`absolute -left-[9px] flex items-center justify-center w-[18px] h-[18px] rounded-full border ${isSynthetic ? "bg-muted/60 border-border/60" : "bg-background border-border"}`}>
                              <EventIcon type={entry.type} />
                            </span>
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className={`text-sm font-medium leading-tight ${isSynthetic ? "text-muted-foreground" : ""}`}>
                                    {eventLabel(entry.type)}
                                  </p>
                                  {isSynthetic && (
                                    <span className="text-[10px] text-muted-foreground/70 bg-muted/50 px-1 py-0.5 rounded leading-none">from invoice</span>
                                  )}
                                </div>
                                {meta && <p className="text-xs text-muted-foreground mt-0.5">{meta}</p>}
                              </div>
                              <time className="text-xs text-muted-foreground shrink-0 pt-0.5">
                                {format(new Date(entry.created * 1000), "MMM d, yyyy h:mm a")}
                              </time>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setRawOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
              >
                <span>Raw Invoice JSON</span>
                {rawOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {rawOpen && inv && (
                <pre className="text-xs overflow-x-auto p-4 bg-muted/20 max-h-64 overflow-y-auto leading-relaxed">
                  {JSON.stringify(inv, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
